import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

const { DATADOG_RUM_APP_ID, DATADOG_RUM_CLIENT_TOKEN, DATADOG_RUM_ENV } = process.env;

const MODULE_NAME = 'login-pa-module';

/**
 * Checks if Datadog RUM is already initialized
 * This is important for micro-frontend architectures where the parent app may have already initialized RUM
 */
const isRumInitialized = (): boolean => {
  try {
    const initConfig = datadogRum.getInitConfiguration();
    return initConfig !== undefined;
  } catch (error) {
    return false;
  }
};

/**
 * Adds module context to existing RUM instance
 * This helps identify events coming from this specific micro-frontend module
 */
const addModuleContext = (): void => {
  try {
    datadogRum.setGlobalContextProperty('service', MODULE_NAME);
    datadogRum.setGlobalContextProperty('module', MODULE_NAME);
    datadogRum.setGlobalContextProperty(
      'module_version',
      process.env.npm_package_version || 'unknown'
    );

    // eslint-disable-next-line no-console
    console.log(`[Datadog RUM] Added context for module: ${MODULE_NAME}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Datadog RUM] Failed to add module context:', error);
  }
};

/**
 * Initializes Datadog RUM for the login-pa-module
 *
 * In a micro-frontend architecture:
 * - If RUM is already initialized by the parent app, it will just add module context
 * - If RUM is not initialized, it will initialize it (useful for standalone development)
 */
export const initDatadogRum = (): void => {
  if (isRumInitialized()) {
    // eslint-disable-next-line no-console
    console.log(
      '[Datadog RUM] RUM already initialized by parent application, adding module context'
    );
    addModuleContext();
    return;
  }

  if (!DATADOG_RUM_APP_ID || !DATADOG_RUM_CLIENT_TOKEN) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Datadog RUM] Missing required environment variables. RUM will not be initialized.'
    );
    return;
  }

  try {
    datadogRum.init({
      applicationId: DATADOG_RUM_APP_ID,
      clientToken: DATADOG_RUM_CLIENT_TOKEN,
      site: 'us5.datadoghq.com',
      service: MODULE_NAME,
      env: DATADOG_RUM_ENV || 'dev',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackBfcacheViews: true,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })]
    });

    addModuleContext();

    // eslint-disable-next-line no-console
    console.log(`[Datadog RUM] Initialized for module: ${MODULE_NAME}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Datadog RUM] Failed to initialize:', error);
  }
};
